import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';

import { useAuthStore } from '../../stores/auth-store';
import { engineUrl } from '../runtime-config';

import { WS_EVENTS } from './events';

import type { WsEventEnvelope, WsEventName } from './events';
import type { ReactNode } from 'react';
import type { Socket } from 'socket.io-client';

/**
 * WebSocketProvider — conexão Socket.IO ao Engine.
 *
 * **Estados:** `disconnected` | `connecting` | `connected` | `error`.
 * Reconexão automática (Socket.IO nativa) com backoff exponencial.
 *
 * **Idempotência cliente:** mantém Set de últimos 200 eventIds para
 * deduplicar quando reconexão re-entrega evento já consumido.
 *
 * **Autenticação:** JWT do auth store enviado no handshake (`auth.token`).
 * Se store não tem token, conexão fica em `disconnected` (deferida).
 */

export type WsStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface WebSocketCtx {
  status: WsStatus;
  /** Inscreve em um evento. Retorna função de unsubscribe. */
  subscribe: <T = unknown>(
    event: WsEventName,
    handler: (envelope: WsEventEnvelope<T>) => void,
  ) => () => void;
}

const Ctx = createContext<WebSocketCtx | null>(null);

const ENGINE_URL = engineUrl();

const DEDUP_MAX = 200;

export function WebSocketProvider({ children }: { children: ReactNode }): JSX.Element {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [status, setStatus] = useState<WsStatus>('disconnected');
  const socketRef = useRef<Socket | null>(null);
  const seenEventIds = useRef<Set<string>>(new Set());
  const listenersRef = useRef<Map<WsEventName, Set<(envelope: WsEventEnvelope) => void>>>(
    new Map(),
  );

  useEffect(() => {
    if (accessToken === null) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setStatus('disconnected');
      return;
    }

    setStatus('connecting');
    const socket = io(`${ENGINE_URL}/ws`, {
      transports: ['websocket'],
      auth: { token: accessToken },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.3,
    });

    socket.on('connect', () => setStatus('connected'));
    socket.on('disconnect', () => setStatus('disconnected'));
    socket.on('connect_error', () => setStatus('error'));

    // Roteamento de eventos para listeners registrados via subscribe().
    const dispatch = (eventName: WsEventName) => (envelope: WsEventEnvelope) => {
      // Idempotência cliente
      if (typeof envelope.eventId === 'string' && seenEventIds.current.has(envelope.eventId)) {
        return;
      }
      seenEventIds.current.add(envelope.eventId);
      if (seenEventIds.current.size > DEDUP_MAX) {
        // Drop oldest — Set mantém ordem de inserção
        const first = seenEventIds.current.values().next().value;
        if (first !== undefined) seenEventIds.current.delete(first);
      }
      const listeners = listenersRef.current.get(eventName);
      if (listeners) {
        for (const handler of listeners) handler(envelope);
      }
    };

    // Bind universal sobre WS_EVENTS — source-of-truth única evita drift.
    for (const ev of Object.values(WS_EVENTS)) {
      socket.on(ev, dispatch(ev));
    }

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [accessToken]);

  const value = useMemo<WebSocketCtx>(
    () => ({
      status,
      subscribe: <T,>(event: WsEventName, handler: (envelope: WsEventEnvelope<T>) => void) => {
        let listeners = listenersRef.current.get(event);
        if (!listeners) {
          listeners = new Set();
          listenersRef.current.set(event, listeners);
        }
        const wrapped = handler as (envelope: WsEventEnvelope) => void;
        listeners.add(wrapped);
        return () => {
          listeners?.delete(wrapped);
        };
      },
    }),
    [status],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWebSocket(): WebSocketCtx {
  const ctx = useContext(Ctx);
  if (ctx === null) {
    throw new Error('useWebSocket deve ser usado dentro de <WebSocketProvider>');
  }
  return ctx;
}
